import { Controller } from "@hotwired/stimulus";

// Progressive enhancement for ActionMenuComponent. Native <details>/<summary> keeps every
// operation reachable without JavaScript; this layer coordinates multiple menus, restores
// focus after Escape and pins an open panel to the viewport so clipped cards and tables cannot
// cut it off.
export default class extends Controller {
  static targets = ["trigger", "panel"];

  connect() {
    this.outsideClickHandler = this.closeIfOutside.bind(this);
    this.escapeHandler = this.closeOnEscape.bind(this);
    this.viewportChangeHandler = this.closeOnViewportChange.bind(this);
    this.beforeCacheHandler = this.close.bind(this);

    document.addEventListener("click", this.outsideClickHandler);
    document.addEventListener("keydown", this.escapeHandler);
    document.addEventListener("scroll", this.viewportChangeHandler, true);
    window.addEventListener("resize", this.viewportChangeHandler);
    document.addEventListener("turbo:before-cache", this.beforeCacheHandler);
    this.sync();
  }

  disconnect() {
    document.removeEventListener("click", this.outsideClickHandler);
    document.removeEventListener("keydown", this.escapeHandler);
    document.removeEventListener("scroll", this.viewportChangeHandler, true);
    window.removeEventListener("resize", this.viewportChangeHandler);
    document.removeEventListener("turbo:before-cache", this.beforeCacheHandler);
    this.resetPanel();
  }

  toggled() {
    if (this.open) {
      document.querySelectorAll("details[data-controller~='action-menu'][open]").forEach((menu) => {
        if (menu !== this.element) menu.removeAttribute("open");
      });
      this.positionPanel();
    } else {
      this.resetPanel();
    }

    this.syncExpanded();
  }

  close({ restoreFocus = false } = {}) {
    if (!this.open) return;

    this.element.removeAttribute("open");
    this.resetPanel();
    this.syncExpanded();
    if (restoreFocus) this.triggerTarget.focus();
  }

  closeIfOutside(event) {
    if (this.open && !this.element.contains(event.target)) this.close();
  }

  closeOnEscape(event) {
    if (!this.open || event.key !== "Escape") return;

    event.preventDefault();
    this.close({ restoreFocus: true });
  }

  closeOnViewportChange(event) {
    if (!this.open) return;
    if (event.target instanceof Node && this.panelTarget.contains(event.target)) return;

    this.close();
  }

  sync() {
    this.syncExpanded();
    if (this.open) this.positionPanel();
    else this.resetPanel();
  }

  syncExpanded() {
    this.triggerTarget.setAttribute("aria-expanded", this.open ? "true" : "false");
  }

  positionPanel() {
    const gutter = 16;
    const gap = 4;
    const triggerRect = this.triggerTarget.getBoundingClientRect();
    // innerWidth includes the vertical scrollbar. On long pages (notably the calendar) that
    // made a right-aligned panel overflow the actually visible viewport by its full width.
    const viewportWidth = document.documentElement.clientWidth;
    const maxWidth = viewportWidth - (2 * gutter);

    Object.assign(this.panelTarget.style, {
      position: "fixed",
      left: "0px",
      right: "auto",
      top: "0px",
      marginTop: "0px",
      width: "max-content",
      maxWidth: `${maxWidth}px`,
    });

    const measured = this.panelTarget.getBoundingClientRect();
    const width = Math.min(Math.max(measured.width, triggerRect.width), maxWidth);
    const alignedLeft = this.element.classList.contains("action-menu--left")
      ? triggerRect.left
      : triggerRect.right - width;
    const left = Math.min(Math.max(alignedLeft, gutter), viewportWidth - width - gutter);

    this.panelTarget.style.width = `${width}px`;
    const height = this.panelTarget.getBoundingClientRect().height;
    const roomBelow = window.innerHeight - triggerRect.bottom - gap - gutter;
    const top = roomBelow >= height
      ? triggerRect.bottom + gap
      : Math.max(gutter, triggerRect.top - height - gap);

    Object.assign(this.panelTarget.style, { left: `${left}px`, top: `${top}px` });
  }

  resetPanel() {
    this.panelTarget.removeAttribute("style");
  }

  get open() {
    return this.element.open;
  }
}
